import { useQueries } from "@tanstack/react-query";

import { getStage, stageKey, type FixFirst, type PipelineTile, type StageThresholds } from "../api";
import { fixFirstMeasureLines, measureChipClass } from "./measure";

const HEADING: Record<string, string> = {
  fix: "Fix first",
  watch: "No red stages. Watch these.",
  none: "Nothing needs fixing this {noun}.",
};

const SUBHEAD: Record<string, string> = {
  fix: "red steps, worst first",
  watch: "amber steps to keep an eye on",
  none: "",
};

/** The short id printed on a card's right (`s06_pdf_waterfall` -> `s06`). */
function shortId(key: string): string {
  return key.match(/^[a-z]+\d+/)?.[0] ?? key;
}

/**
 * The fix-first strip (UX §4 F1) — the week's headline, as a 4-column card
 * grid with the worst item emphasised (UX-mockup prototype v2, frame 1
 * `.grid4`/`.card.first`). `fix_first.items` is already ranked by CVCE (dev
 * spec I-4); this card lists them in that order and never re-sorts. Each
 * card fetches its own stage's thresholds so its red/amber sentences can
 * name the cut-off, same as the Stage detail screen does.
 */
export function FixFirstCard({
  fixFirst,
  tilesByKey,
  onOpen,
  weekStart,
  noun = "week",
}: {
  fixFirst: FixFirst;
  tilesByKey: Map<string, PipelineTile>;
  onOpen: (stageKey: string) => void;
  weekStart: string;
  /** "week" or "month" — cvce-pipeline-4's grain toggle names the period in the "nothing to fix" heading. */
  noun?: "week" | "month";
}) {
  const heading = (HEADING[fixFirst.mode] ?? HEADING.none).replace("{noun}", noun);
  const subhead = SUBHEAD[fixFirst.mode] ?? "";
  const shown = fixFirst.items.slice(0, 4);
  const rest = fixFirst.items.slice(4);

  const thresholdQueries = useQueries({
    queries: shown.map((key) => ({
      queryKey: stageKey(key, weekStart, 1),
      queryFn: () => getStage(key, weekStart, 1),
    })),
  });
  const thresholdsByKey = new Map<string, StageThresholds>();
  shown.forEach((key, i) => {
    const t = thresholdQueries[i]?.data?.data.thresholds;
    if (t) thresholdsByKey.set(key, t);
  });

  return (
    <section aria-labelledby="pipeline-fix-first" className="flex flex-col gap-2">
      <h2 id="pipeline-fix-first" className="text-base font-semibold text-ks-ink">
        {heading} {subhead && <span className="font-normal text-ks-grey-1">{subhead}</span>}
      </h2>
      {shown.length > 0 && (
        <ol data-fix-first className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {shown.map((stageKeyValue, index) => {
            const tile = tilesByKey.get(stageKeyValue);
            const lines = tile ? fixFirstMeasureLines(tile, thresholdsByKey.get(stageKeyValue)) : [];
            return (
              <li key={stageKeyValue}>
                <button
                  type="button"
                  data-nonmutating
                  onClick={() => onOpen(stageKeyValue)}
                  className={`flex w-full flex-col gap-1.5 rounded-ks-md border bg-ks-surface p-3 text-left ${
                    index === 0 ? "border-2 border-ks-purple bg-ks-purple-4" : "border-ks-grey-4"
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-ks-ink">
                      {index === 0 ? "▶ " : ""}
                      {tile?.step ?? ""} {"·"} {tile?.label ?? stageKeyValue}
                    </span>
                    <span className="font-mono text-[11px] text-ks-grey-1">{shortId(stageKeyValue)}</span>
                  </span>
                  {lines.map((line) => (
                    <span key={line.letter} className="flex items-center gap-1.5 text-xs text-ks-ink">
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-bold ${measureChipClass(line.rag)}`}
                        aria-hidden
                      >
                        {line.letter}
                      </span>
                      {line.text}
                    </span>
                  ))}
                  {tile && <span className="text-[11px] text-ks-grey-1">n = {tile.n}</span>}
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {rest.length > 0 && (
        <p className="text-[11px] text-ks-grey-1">
          +{rest.length} more {fixFirst.mode === "fix" ? "red" : "amber"}:{" "}
          {rest.map((k) => tilesByKey.get(k)?.label ?? k).join(", ")}
        </p>
      )}
    </section>
  );
}
